import { MsGraphApiService } from "../services/MsGraphApiService";
import { OutlookApiService } from "../services/OutlookApiService";

export class AttachmentDataHelpers {
    static replaceCIDReferences(body: string, attachment: any) {
        // Extract CID references and replace them with base64 or URL images
        let processedBody = this.formatHtml(body);
        const cidMatches = body.match(/cid:([^'"\s>]+)/g);

        if (cidMatches) {
            for (const cid of cidMatches) {
                const contentId = cid.replace("cid:", "");
                const result = attachment.value.find((x: any) => x.contentId === contentId);
                if (result) {
                    const base64Image = `data:${result.contentType};base64,${result.contentBytes}`;
                    processedBody = processedBody.replace(cid, base64Image);
                }
            }
        }
        return this.adjustImageStyles(processedBody);
    }

    static async replaceCIDReferencesDirectly(body: string, inlineImagesAsBlobs: Blob[]) {
        let processedBody = this.formatHtml(body);
        const cidMatches = body.match(/cid:([^'"\s>]+)/g);
        if (!cidMatches?.length) {
            return processedBody;
        }

        const base64Strings = await Promise.all(
            inlineImagesAsBlobs.map((blob) => {
                return new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });
            })
        );

        cidMatches.forEach((cid, i) => {
            if (base64Strings[i]) {
                processedBody = processedBody.replace(cid, base64Strings[i]);
            }
        });

        return this.adjustImageStyles(processedBody);
    }

    static formatHtml(html: string): string {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        doc.querySelectorAll("td").forEach((td) => {
            td.style.wordBreak = "normal";
            td.style.whiteSpace = "normal";
            td.style.minWidth = "50px";
        });

        return doc.body.innerHTML;
    }

    static adjustImageStyles(html: string): string {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");
        doc.querySelectorAll("img").forEach((img) => {
            img.style.maxWidth = "none";
        });
        return doc.body.innerHTML;
    }

    static partitionAttachments(attachments: Office.AttachmentDetails[]) {
        const inlineAttachments = [];
        const attachmentsNoCloud = [];
        for (const attachment of attachments) {
            if (attachment.isInline) {
                inlineAttachments.push(attachment);
            } else if (!attachment.isInline && attachment.attachmentType !== Office.MailboxEnums.AttachmentType.Cloud) {
                attachmentsNoCloud.push(attachment);
            }
        }
        return { inlineAttachments, attachmentsNoCloud };
    }

    static async getHtmlBodyWithInlineAttachments(
        msGraphApiService: MsGraphApiService,
        outlookApiService: OutlookApiService,
        isNL1221Supported: boolean,
        inlineAttachments: Office.AttachmentDetails[]
    ): Promise<string> {
        const currentClearMessageBody = await msGraphApiService.getCurrentClearMessageBody(isNL1221Supported);
        if (!isNL1221Supported) {
            return currentClearMessageBody.trim();
        }
        const inlineAttachmentsAsBlobs = await Promise.all(
            inlineAttachments.map((attachment) => outlookApiService.getAttachmentContentDirect(attachment.id))
        );
        const htmlBodyWithInlineAttachments = await AttachmentDataHelpers.replaceCIDReferencesDirectly(
            currentClearMessageBody,
            inlineAttachmentsAsBlobs
        );
        return htmlBodyWithInlineAttachments.trim();
    }
}
